import { useState } from 'react';
import BrandLogo from '@/components/BrandLogo';
import StandaloneSurface from '@/components/Layout/StandaloneSurface';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AppIcon } from '@/components/ui/icons';
import { Button } from '@/features/ui/components/Button';
import { useAuth } from '@/contexts/AuthContext';
import TwoFactorSetupModal from '@/features/user/components/TwoFactorSetupModal';

/** Mandatory first-time enrollment shown before the product shell can mount. */
const MfaEnrollmentRequired = () => {
  const { cancelMfa, pendingMfaEmail } = useAuth();
  const [showSetup, setShowSetup] = useState(false);

  return (
    <StandaloneSurface measure="reading">
      <div className="space-y-6">
        <BrandLogo variant="auth" className="mx-auto" />
        <Card variant="elevated">
          <CardHeader className="items-center text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-status-warning-soft">
              <AppIcon name="shield" className="h-5 w-5 text-status-warning-foreground" />
            </div>
            <CardTitle className="mt-3">Set up two-factor authentication</CardTitle>
            {pendingMfaEmail && <p className="text-sm font-medium text-text-secondary">{pendingMfaEmail}</p>}
          </CardHeader>

          <CardContent className="space-y-5">
            <Alert variant="warning">
              <AppIcon name="alertCircle" className="h-4 w-4" />
              <AlertTitle>Required before workspace access</AlertTitle>
              <AlertDescription>
                AX1 requires an authenticator app for every connected Staging and Production session.
              </AlertDescription>
            </Alert>

            <div className="space-y-3 text-sm text-text-muted">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent-primary/10">
                  <AppIcon name="smartphone" className="h-4 w-4 text-accent-primary" />
                </div>
                <p>Use Google Authenticator, Microsoft Authenticator, Authy, 1Password, or another TOTP app.</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent-primary/10 text-xs font-semibold text-accent-primary">2</div>
                <p>Scan the QR code, then verify the six-digit code. Workspace data stays blocked until Supabase confirms AAL2.</p>
              </div>
            </div>

            <Button
              type="button"
              size="lg"
              fullWidth
              onClick={() => setShowSetup(true)}
              endIcon={<AppIcon name="arrowRight" className="h-4 w-4" />}
            >
              Set up authenticator
            </Button>
            <Button type="button" variant="ghost" fullWidth onClick={cancelMfa}>
              Sign out and return to login
            </Button>
          </CardContent>
        </Card>
      </div>

      <TwoFactorSetupModal
        isOpen={showSetup}
        onClose={() => setShowSetup(false)}
        onSuccess={() => window.location.reload()}
      />
    </StandaloneSurface>
  );
};

export default MfaEnrollmentRequired;
